import * as WebGl2ShaderUtils from "#webgl2/webgl2-shader-utils"
import {checkForGlError} from "#webgl2/helpers/gl-error"
import type {ColorLike, Vector2Like} from "@cm/math"
import {isColorLike, isVector2Like} from "@cm/math"
import {WebGl2Settings} from "#webgl2/settings"

export class WebGl2GeometryVertexAttribute {
    constructor(
        private gl: WebGL2RenderingContext,
        readonly dimensionality: number,
        initialCapacity = 1024,
    ) {
        WebGl2GeometryVertexAttribute.instances?.add(this)
        this.allocate(initialCapacity)
    }

    dispose(): void {
        if (this._buffer) {
            this.gl.deleteBuffer(this._buffer)
            this._buffer = undefined
        }
        this._data = undefined
        WebGl2GeometryVertexAttribute.instances?.delete(this)
    }

    get size(): number {
        return this._numElements
    }

    get data(): Readonly<Float32Array> {
        if (!this._data) {
            throw Error("WebGl2GeometryVertexAttribute: No data allocated")
        }
        return this._data.subarray(0, this._numElements * this.dimensionality)
    }

    clear() {
        this._numElements = 0
    }

    // overwrite data at a specific index
    // set(index: number, data: Float32Array) {
    //     if (data.length % this.dimensionality !== 0) {
    //         throw Error("WebGl2GeometryVertexAttribute: Data length must be a multiple of dimensionality")
    //     }
    //     const numElements = data.length / this.dimensionality
    //     if (numElements === 0) {
    //         return
    //     }
    //     if (index < 0 || index + numElements > this._numElements) {
    //         throw Error("WebGl2GeometryVertexAttribute: Index out of bounds")
    //     }
    //     if (!this._data) {
    //         throw Error("WebGl2GeometryVertexAttribute: No data allocated")
    //     }
    //     this._data.set(data, index * this.dimensionality)
    // }

    add(data: Vector2Like[] | ColorLike[] | number[] | Float32Array) {
        const isNumberArray = (arr: unknown): arr is number[] => {
            return Array.isArray(arr) && typeof arr[0] === "number"
        }
        const numElements = Array.isArray(data) && !isNumberArray(data) ? data.length : data.length / this.dimensionality
        if (numElements === 0) {
            return
        }
        this.reserve(this._numElements + numElements)
        if (!this._data) {
            throw Error("WebGl2GeometryVertexAttribute: No data allocated")
        }
        if (data instanceof Float32Array || isNumberArray(data)) {
            if (data.length % this.dimensionality !== 0) {
                throw Error("WebGl2GeometryVertexAttribute: Data length must be a multiple of dimensionality")
            }
            this._data.set(data, this._numElements * this.dimensionality)
        } else {
            let index = this._numElements * this.dimensionality
            if (isVector2Like(data[0])) {
                if (this.dimensionality !== 2) {
                    throw Error("WebGl2GeometryVertexAttribute: Dimensionality must be 2 for Vector2Like data")
                }
                for (let i = 0; i < numElements; i++) {
                    const v = data[i] as Vector2Like
                    this._data[index++] = v.x
                    this._data[index++] = v.y
                }
            } else if (isColorLike(data[0])) {
                if (this.dimensionality !== 4) {
                    throw Error("WebGl2GeometryVertexAttribute: Dimensionality must be 4 for ColorLike data")
                }
                for (let i = 0; i < numElements; i++) {
                    const v = data[i] as ColorLike
                    this._data[index++] = v.r
                    this._data[index++] = v.g
                    this._data[index++] = v.b
                    this._data[index++] = v.a ?? 1
                }
            } else {
                throw Error("WebGl2GeometryVertexAttribute: Unsupported data type")
            }
        }
        this._numElements += numElements
        this._needsUpload = true
    }

    remove(startIndex: number, count?: number): void {
        count ??= this._numElements - startIndex
        if (startIndex < 0 || startIndex + count > this._numElements) {
            throw Error("Invalid vertex range")
        }
        if (!this._data) {
            throw Error("WebGl2GeometryVertexAttribute: No data allocated")
        }
        this._data.set(
            this._data.subarray((startIndex + count) * this.dimensionality, this._numElements * this.dimensionality),
            startIndex * this.dimensionality,
        )
        this._numElements -= count
        this._needsUpload = true
    }

    bind(location: number): void {
        if (!this._buffer) {
            throw Error("WebGl2GeometryVertexAttribute: No buffer allocated")
        }
        this.updateBuffer()
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this._buffer)
        this.gl.vertexAttribPointer(location, this.dimensionality, this.gl.FLOAT, false, 0, 0)
        this.gl.enableVertexAttribArray(location)
        checkForGlError(this.gl)
    }

    private updateBuffer() {
        if (!this._needsUpload) {
            return
        }
        if (!this._data || !this._buffer) {
            throw Error("WebGl2GeometryVertexAttribute: No data or buffer allocated")
        }
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this._buffer)
        this.gl.bufferSubData(this.gl.ARRAY_BUFFER, 0, this._data)
        checkForGlError(this.gl)
        this._needsUpload = false
    }

    private reserve(numElements: number): void {
        if (numElements <= this._capacity) {
            return
        }
        const newCapacity = Math.max(this._capacity * 2, numElements)
        this.allocate(newCapacity)
        this._needsUpload = true
    }

    private allocate(newCapacity: number): void {
        if (newCapacity <= 0) {
            throw Error("WebGl2GeometryVertexAttribute: Capacity must be greater than 0")
        }
        const prevData = this._data
        this._capacity = newCapacity
        this._data = new Float32Array(this._capacity * this.dimensionality)
        if (prevData) {
            this._data.set(prevData)
        }
        if (this._buffer) {
            this.gl.deleteBuffer(this._buffer)
        }
        this._buffer = WebGl2ShaderUtils.createDynamicBuffer(this.gl, this.gl.ARRAY_BUFFER, this._capacity * this.dimensionality * 4) // 32bit float
    }

    private _capacity = 0
    private _numElements = 0
    private _data?: Float32Array
    private _buffer?: WebGLBuffer
    private _needsUpload = false

    static instances = WebGl2Settings.InstanceTracking ? new Set<WebGl2GeometryVertexAttribute>() : undefined
}
